import { doc, getDoc, runTransaction, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

const TARGET_WORK_MINUTES = 8 * 60;
const NON_DEDUCTIBLE_BREAK_TYPES = new Set(['Stadtfahrt', 'Langstrecke']);

const pad = (value) => String(value).padStart(2, '0');

export const formatDateKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const eachDateKey = (startDate, endDate) => {
  const keys = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  while (current <= end) {
    keys.push(formatDateKey(current));
    current.setDate(current.getDate() + 1);
  }

  return keys;
};

const getWorkTimeDateKeys = (startDate, endDate) => {
  const firstDateToRead = new Date(startDate);
  firstDateToRead.setDate(firstDateToRead.getDate() - 1);
  return eachDateKey(firstDateToRead, endDate);
};

export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatMinutes = (minutes) => {
  const safeMinutes = Math.max(0, Math.round(minutes || 0));
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;
  return `${hours}h ${mins.toString().padStart(2, '0')}min`;
};

export const isDeductibleBreakType = (type) => (
  !NON_DEDUCTIBLE_BREAK_TYPES.has(type || 'Pause')
);

const overlapMinutes = (start, end, rangeStart, rangeEnd) => {
  if (!start || !end || !rangeStart || !rangeEnd) return 0;
  const from = Math.max(start.getTime(), rangeStart.getTime());
  const to = Math.min(end.getTime(), rangeEnd.getTime());
  return Math.max(0, Math.round((to - from) / 60000));
};

const getBreakEnd = (breakItem, fallbackNow) => {
  const startTime = toDate(breakItem.startTime);
  const explicitEnd = toDate(breakItem.endTime);
  if (explicitEnd) return explicitEnd;
  if (!startTime) return null;

  const plannedDuration = Number(breakItem.plannedDuration || 30);
  const plannedEnd = new Date(startTime.getTime() + plannedDuration * 60000);
  return plannedEnd > fallbackNow ? fallbackNow : plannedEnd;
};

const normalizeBreaks = (breaks = [], rangeStart, rangeEnd, fallbackNow) => {
  return breaks
    .map((breakItem, index) => {
      const startTime = toDate(breakItem.startTime);
      const endTime = getBreakEnd(breakItem, fallbackNow);
      const minutes = overlapMinutes(startTime, endTime, rangeStart, rangeEnd);
      const type = breakItem.type || 'Pause';

      return {
        id: breakItem.id || `break-${startTime?.getTime() || index}-${index}`,
        startTime,
        endTime,
        type,
        plannedDuration: breakItem.plannedDuration ?? null,
        correctionReason: breakItem.correctionReason || '',
        source: breakItem.source || 'driver_app',
        breakIndex: index,
        minutes,
        isDeductible: isDeductibleBreakType(type),
        isOpen: !breakItem.endTime,
      };
    })
    .filter((breakItem) => breakItem.startTime && breakItem.endTime && breakItem.minutes > 0);
};

const normalizeOfflinePeriods = (offlinePeriods = [], rangeStart, rangeEnd, fallbackNow) => {
  return offlinePeriods
    .map((period, index) => {
      const startTime = toDate(period.offlineStart);
      const endTime = toDate(period.offlineEnd) || fallbackNow;
      const minutes = overlapMinutes(startTime, endTime, rangeStart, rangeEnd);

      return {
        id: period.id || `offline-${startTime?.getTime() || index}-${index}`,
        startTime,
        endTime,
        reason: period.reason || 'offline',
        minutes,
        isOpen: !period.offlineEnd,
      };
    })
    .filter((period) => period.startTime && period.endTime && period.minutes > 0);
};

const getDisplayShiftEnd = (explicitShiftEnd, offlinePeriods) => {
  if (!explicitShiftEnd) return null;

  const finalOfflinePeriod = offlinePeriods
    .filter(period => (
      period.startTime &&
      period.startTime < explicitShiftEnd &&
      (!period.endTime || Math.abs(period.endTime.getTime() - explicitShiftEnd.getTime()) < 60000)
    ))
    .sort((a, b) => b.startTime.getTime() - a.startTime.getTime())[0];

  return finalOfflinePeriod?.startTime || explicitShiftEnd;
};

const normalizeShift = ({
  dateKey,
  driverId,
  driverData,
  shift,
  shiftIndex,
  rangeStart,
  rangeEnd,
  now,
}) => {
  const shiftStart = toDate(shift.shiftStart);
  if (!shiftStart) return null;

  const explicitShiftEnd = toDate(shift.shiftEnd);
  const isActive = shift.isActive === true && !explicitShiftEnd;
  const effectiveEnd = explicitShiftEnd || (isActive ? now : shiftStart);

  if (!effectiveEnd || effectiveEnd <= rangeStart || shiftStart >= rangeEnd) {
    return null;
  }

  const calculatedRangeStart = new Date(Math.max(shiftStart.getTime(), rangeStart.getTime()));
  const calculatedRangeEnd = new Date(Math.min(effectiveEnd.getTime(), rangeEnd.getTime()));

  const spanMinutes = overlapMinutes(shiftStart, effectiveEnd, rangeStart, rangeEnd);
  if (spanMinutes <= 0) return null;

  const breaks = normalizeBreaks(shift.breaks || [], calculatedRangeStart, calculatedRangeEnd, now);
  const offlinePeriods = normalizeOfflinePeriods(
    shift.offlinePeriods || [],
    calculatedRangeStart,
    calculatedRangeEnd,
    now
  );

  const breakMinutes = breaks.reduce(
    (sum, breakItem) => sum + (breakItem.isDeductible ? breakItem.minutes : 0),
    0
  );
  const nonDeductibleBreakMinutes = breaks.reduce(
    (sum, breakItem) => sum + (breakItem.isDeductible ? 0 : breakItem.minutes),
    0
  );
  const offlineMinutes = offlinePeriods.reduce((sum, period) => sum + period.minutes, 0);
  const grossMinutes = Math.max(0, spanMinutes - offlineMinutes);
  const netMinutes = Math.max(0, grossMinutes - breakMinutes);
  const targetMinutes = Number(shift.targetMinutes || TARGET_WORK_MINUTES);
  const displayShiftEnd = getDisplayShiftEnd(explicitShiftEnd, offlinePeriods);

  return {
    id: `${dateKey}-${driverId}-${shift.shiftNumber || shiftIndex + 1}`,
    dateKey,
    driverId,
    driverName: driverData.driverName || driverData.name || driverId,
    operatorId: shift.operatorId || driverData.operatorId || '',
    organizationId: shift.organizationId || driverData.organizationId || '',
    shiftNumber: shift.shiftNumber || shiftIndex + 1,
    shiftIndex,
    shiftStart,
    shiftEnd: explicitShiftEnd,
    displayShiftEnd,
    effectiveEnd,
    spanMinutes,
    isActive,
    grossMinutes,
    breakMinutes,
    nonDeductibleBreakMinutes,
    offlineMinutes,
    netMinutes,
    targetMinutes,
    overtimeMinutes: Math.max(0, netMinutes - targetMinutes),
    breaks,
    offlinePeriods,
    breakTypes: Array.from(new Set(breaks.map((breakItem) => breakItem.type))),
    hasOpenItems: isActive || breaks.some((breakItem) => breakItem.isOpen) || offlinePeriods.some((period) => period.isOpen),
  };
};

const getDriverShifts = (driverData = {}) => {
  if (Array.isArray(driverData.shifts)) {
    return driverData.shifts;
  }

  if (!driverData.shiftStart) {
    return [];
  }

  return [{
    shiftNumber: 1,
    shiftStart: driverData.shiftStart,
    shiftEnd: driverData.shiftEnd,
    isActive: driverData.isActive === true,
    breaks: Array.isArray(driverData.breaks) ? driverData.breaks : [],
    offlinePeriods: Array.isArray(driverData.offlinePeriods) ? driverData.offlinePeriods : [],
  }];
};

export const fetchWorkTimeRows = async ({ startDate, endDate }) => {
  const now = new Date();
  const rangeStart = new Date(startDate);
  rangeStart.setHours(0, 0, 0, 0);

  const rangeEnd = new Date(endDate);
  rangeEnd.setHours(23, 59, 59, 999);

  const dateKeys = getWorkTimeDateKeys(rangeStart, rangeEnd);
  const docs = await Promise.all(
    dateKeys.map(async (dateKey) => {
      const snapshot = await getDoc(doc(db, 'shifts', dateKey));
      return snapshot.exists() ? { dateKey, data: snapshot.data() } : null;
    })
  );

  return docs
    .filter(Boolean)
    .flatMap(({ dateKey, data }) => {
      const drivers = data.drivers || {};
      return Object.entries(drivers).flatMap(([driverId, driverData]) => {
        const shifts = getDriverShifts(driverData);
        return shifts
          .map((shift, shiftIndex) => normalizeShift({
            dateKey,
            driverId,
            driverData,
            shift,
            shiftIndex,
            rangeStart,
            rangeEnd,
            now,
          }))
          .filter(Boolean);
      });
    })
    .sort((a, b) => b.shiftStart.getTime() - a.shiftStart.getTime());
};

export const summarizeWorkTimeRows = (rows) => rows.reduce((summary, row) => ({
  grossMinutes: summary.grossMinutes + row.grossMinutes,
  breakMinutes: summary.breakMinutes + row.breakMinutes,
  nonDeductibleBreakMinutes: summary.nonDeductibleBreakMinutes + (row.nonDeductibleBreakMinutes || 0),
  offlineMinutes: summary.offlineMinutes + row.offlineMinutes,
  netMinutes: summary.netMinutes + row.netMinutes,
  overtimeMinutes: summary.overtimeMinutes + row.overtimeMinutes,
  activeShifts: summary.activeShifts + (row.isActive ? 1 : 0),
  shifts: summary.shifts + 1,
}), {
  grossMinutes: 0,
  breakMinutes: 0,
  nonDeductibleBreakMinutes: 0,
  offlineMinutes: 0,
  netMinutes: 0,
  overtimeMinutes: 0,
  activeShifts: 0,
  shifts: 0,
});

const createBreakId = () => (
  `web-break-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
);

const toTimestamp = (date) => Timestamp.fromDate(new Date(date));

const getShiftIndex = (shifts, shiftNumber, fallbackShiftIndex) => {
  const byNumber = shifts.findIndex((shift) => Number(shift.shiftNumber) === Number(shiftNumber));
  if (byNumber !== -1) return byNumber;
  return Number.isInteger(fallbackShiftIndex) ? fallbackShiftIndex : -1;
};

const normalizeBreakInput = (breakData, existingBreak = {}) => {
  const startTime = new Date(breakData.startTime);
  const endTime = new Date(breakData.endTime);

  if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime())) {
    throw new Error('Start und Ende der Pause müssen gültige Zeitpunkte sein.');
  }

  if (endTime <= startTime) {
    throw new Error('Das Pausenende muss nach dem Pausenbeginn liegen.');
  }

  const plannedDuration = Math.max(1, Math.round((endTime.getTime() - startTime.getTime()) / 60000));

  return {
    ...existingBreak,
    id: existingBreak.id || breakData.id || createBreakId(),
    type: breakData.type || existingBreak.type || 'Pause',
    startTime: toTimestamp(startTime),
    endTime: toTimestamp(endTime),
    plannedDuration,
    source: existingBreak.source || breakData.source || 'web_admin',
    correctionReason: breakData.correctionReason || existingBreak.correctionReason || '',
    updatedAt: Timestamp.now(),
  };
};

const normalizeShiftInput = (shiftData, existingShift = {}, dateKey) => {
  const shiftStart = new Date(shiftData.shiftStart);
  const shiftEnd = new Date(shiftData.shiftEnd);

  if (Number.isNaN(shiftStart.getTime()) || Number.isNaN(shiftEnd.getTime())) {
    throw new Error('Start und Ende der Schicht müssen gültige Zeitpunkte sein.');
  }

  if (shiftEnd <= shiftStart) {
    throw new Error('Das Schichtende muss nach dem Schichtbeginn liegen.');
  }

  if (formatDateKey(shiftStart) !== dateKey) {
    throw new Error('Der Schichtbeginn muss im gespeicherten Tagesdokument bleiben.');
  }

  const closeOpenBreaks = (existingShift.breaks || []).map((breakItem) => {
    if (breakItem.endTime) return breakItem;
    return {
      ...breakItem,
      endTime: toTimestamp(shiftEnd),
      updatedAt: Timestamp.now(),
    };
  });

  const closeOpenOfflinePeriods = (existingShift.offlinePeriods || []).map((period) => {
    if (period.offlineEnd) return period;
    return {
      ...period,
      offlineEnd: toTimestamp(shiftEnd),
      updatedAt: Timestamp.now(),
    };
  });

  return {
    ...existingShift,
    shiftStart: toTimestamp(shiftStart),
    shiftEnd: toTimestamp(shiftEnd),
    isActive: false,
    breaks: closeOpenBreaks,
    offlinePeriods: closeOpenOfflinePeriods,
    shiftCorrectionReason: shiftData.correctionReason || existingShift.shiftCorrectionReason || '',
    shiftCorrectionSource: shiftData.source || 'web_admin',
    shiftCorrectedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
};

const assertNoBreakOverlap = (breaks, editedIndex) => {
  const ranges = breaks
    .map((breakItem, index) => ({
      index,
      start: toDate(breakItem.startTime),
      end: toDate(breakItem.endTime),
    }))
    .filter((range) => range.start && range.end);

  const edited = ranges.find((range) => range.index === editedIndex);
  if (!edited) return;

  const overlaps = ranges.some((range) => (
    range.index !== editedIndex &&
    edited.start < range.end &&
    edited.end > range.start
  ));

  if (overlaps) {
    throw new Error('Die Pause überschneidet sich mit einer bestehenden Pause.');
  }
};

export const updateWorkTimeShift = async ({
  dateKey,
  driverId,
  shiftNumber,
  shiftIndex,
  shiftData,
}) => {
  await runTransaction(db, async (transaction) => {
    const shiftRef = doc(db, 'shifts', dateKey);
    const snapshot = await transaction.get(shiftRef);
    if (!snapshot.exists()) throw new Error('Schichttag nicht gefunden.');

    const data = snapshot.data();
    const driverData = data.drivers?.[driverId];
    if (!driverData?.shifts?.length) throw new Error('Fahrerschicht nicht gefunden.');

    const shifts = driverData.shifts.map((shift) => ({ ...shift }));
    const targetShiftIndex = getShiftIndex(shifts, shiftNumber, shiftIndex);
    if (targetShiftIndex < 0 || !shifts[targetShiftIndex]) throw new Error('Schicht nicht gefunden.');

    shifts[targetShiftIndex] = normalizeShiftInput(
      shiftData,
      shifts[targetShiftIndex],
      dateKey
    );

    transaction.update(shiftRef, {
      [`drivers.${driverId}.shifts`]: shifts,
    });
  });
};

export const addWorkTimeBreak = async ({ dateKey, driverId, shiftNumber, shiftIndex, breakData }) => {
  await runTransaction(db, async (transaction) => {
    const shiftRef = doc(db, 'shifts', dateKey);
    const snapshot = await transaction.get(shiftRef);
    if (!snapshot.exists()) throw new Error('Schichttag nicht gefunden.');

    const data = snapshot.data();
    const driverData = data.drivers?.[driverId];
    if (!driverData?.shifts?.length) throw new Error('Fahrerschicht nicht gefunden.');

    const shifts = driverData.shifts.map((shift) => ({ ...shift }));
    const targetShiftIndex = getShiftIndex(shifts, shiftNumber, shiftIndex);
    if (targetShiftIndex < 0 || !shifts[targetShiftIndex]) throw new Error('Schicht nicht gefunden.');

    const breaks = [...(shifts[targetShiftIndex].breaks || [])];
    const newBreak = {
      ...normalizeBreakInput(breakData),
      createdAt: Timestamp.now(),
    };
    breaks.push(newBreak);
    assertNoBreakOverlap(breaks, breaks.length - 1);

    shifts[targetShiftIndex] = {
      ...shifts[targetShiftIndex],
      breaks,
    };

    transaction.update(shiftRef, {
      [`drivers.${driverId}.shifts`]: shifts,
    });
  });
};

export const updateWorkTimeBreak = async ({
  dateKey,
  driverId,
  shiftNumber,
  shiftIndex,
  breakIndex,
  breakData,
}) => {
  await runTransaction(db, async (transaction) => {
    const shiftRef = doc(db, 'shifts', dateKey);
    const snapshot = await transaction.get(shiftRef);
    if (!snapshot.exists()) throw new Error('Schichttag nicht gefunden.');

    const data = snapshot.data();
    const driverData = data.drivers?.[driverId];
    if (!driverData?.shifts?.length) throw new Error('Fahrerschicht nicht gefunden.');

    const shifts = driverData.shifts.map((shift) => ({ ...shift }));
    const targetShiftIndex = getShiftIndex(shifts, shiftNumber, shiftIndex);
    if (targetShiftIndex < 0 || !shifts[targetShiftIndex]) throw new Error('Schicht nicht gefunden.');

    const breaks = [...(shifts[targetShiftIndex].breaks || [])];
    if (!breaks[breakIndex]) throw new Error('Pause nicht gefunden.');

    breaks[breakIndex] = normalizeBreakInput(breakData, breaks[breakIndex]);
    assertNoBreakOverlap(breaks, breakIndex);

    shifts[targetShiftIndex] = {
      ...shifts[targetShiftIndex],
      breaks,
    };

    transaction.update(shiftRef, {
      [`drivers.${driverId}.shifts`]: shifts,
    });
  });
};

export const deleteWorkTimeBreak = async ({ dateKey, driverId, shiftNumber, shiftIndex, breakIndex }) => {
  await runTransaction(db, async (transaction) => {
    const shiftRef = doc(db, 'shifts', dateKey);
    const snapshot = await transaction.get(shiftRef);
    if (!snapshot.exists()) throw new Error('Schichttag nicht gefunden.');

    const data = snapshot.data();
    const driverData = data.drivers?.[driverId];
    if (!driverData?.shifts?.length) throw new Error('Fahrerschicht nicht gefunden.');

    const shifts = driverData.shifts.map((shift) => ({ ...shift }));
    const targetShiftIndex = getShiftIndex(shifts, shiftNumber, shiftIndex);
    if (targetShiftIndex < 0 || !shifts[targetShiftIndex]) throw new Error('Schicht nicht gefunden.');

    const breaks = [...(shifts[targetShiftIndex].breaks || [])];
    if (!breaks[breakIndex]) throw new Error('Pause nicht gefunden.');

    breaks.splice(breakIndex, 1);
    shifts[targetShiftIndex] = {
      ...shifts[targetShiftIndex],
      breaks,
    };

    transaction.update(shiftRef, {
      [`drivers.${driverId}.shifts`]: shifts,
    });
  });
};
